import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Button,
  Drawer,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconEdit, IconPlus, IconToolsKitchen2, IconTrash } from '@tabler/icons-react';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ModifierFormModal } from './ModifierFormModal';
import { useDeleteModifier } from './modifierGroupsApi';
import type { ModifierDto, ModifierGroupDto } from './types';

interface Props {
  opened: boolean;
  onClose: () => void;
  group: ModifierGroupDto | null;
}

export function ManageModifiersDrawer({ opened, onClose, group }: Props) {
  const { t, i18n } = useTranslation();
  const deleteMutation = useDeleteModifier();
  const drawerPosition = i18n.language === 'ar' ? 'left' : 'right';

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ModifierDto | null>(null);
  const [deleting, setDeleting] = useState<ModifierDto | null>(null);

  const sortedModifiers = useMemo(
    () =>
      [...(group?.modifiers ?? [])].sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
        return a.nameAr.localeCompare(b.nameAr);
      }),
    [group?.modifiers],
  );

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (modifier: ModifierDto) => {
    setEditing(modifier);
    setModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting || !group) return;
    try {
      await deleteMutation.mutateAsync({
        groupId: group.modifierGroupId,
        modifierId: deleting.modifierId,
      });
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: deleting.nameAr,
      });
      setDeleting(null);
    } catch {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: t('common.toasts.errorBody'),
      });
    }
  };

  if (!group) return null;
  const hasModifiers = sortedModifiers.length > 0;

  return (
    <>
      <Drawer
        opened={opened}
        onClose={onClose}
        title={t('modifiers.drawer.title', { groupName: group.nameAr })}
        position={drawerPosition}
        size="lg"
        padding="lg"
      >
        <Stack gap="md">
          <Group justify="space-between" align="center">
            <Text fz="sm" c="var(--wheels-ink-muted)">
              {t('modifiers.drawer.subtitle')}
            </Text>
            <Button
              size="xs"
              leftSection={<IconPlus size={14} stroke={1.8} />}
              onClick={openCreate}
            >
              {t('modifiers.drawer.addModifier')}
            </Button>
          </Group>

          <Paper p={hasModifiers ? 0 : 'lg'} withBorder>
            {!hasModifiers ? (
              <EmptyState
                icon={<IconToolsKitchen2 size={20} stroke={1.6} />}
                title={t('modifiers.empty.title')}
                description={t('modifiers.empty.body')}
                actionLabel={t('modifiers.drawer.addModifier')}
                onAction={openCreate}
              />
            ) : (
              <Table verticalSpacing="sm" highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('modifiers.columns.name')}</Table.Th>
                    <Table.Th>{t('modifiers.columns.priceDelta')}</Table.Th>
                    <Table.Th>{t('modifiers.columns.sort')}</Table.Th>
                    <Table.Th>{t('modifiers.columns.status')}</Table.Th>
                    <Table.Th style={{ width: 80 }}></Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sortedModifiers.map((modifier) => (
                    <Table.Tr key={modifier.modifierId}>
                      <Table.Td>
                        <Text fw={500} c="var(--wheels-ink-strong)">
                          {modifier.nameAr}
                        </Text>
                        {modifier.nameEn && (
                          <Text fz="xs" c="var(--wheels-ink-muted)">
                            {modifier.nameEn}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text fz="sm" className="font-numeric">
                          {formatPriceDelta(modifier.priceDelta)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text fz="sm" className="font-numeric">
                          {modifier.sortOrder}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {modifier.isActive ? (
                          <Badge color="green" variant="light" radius="sm" size="sm">
                            {t('common.active')}
                          </Badge>
                        ) : (
                          <Badge color="gray" variant="light" radius="sm" size="sm">
                            {t('common.inactive')}
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4} justify="flex-end" wrap="nowrap">
                          <Tooltip label={t('common.edit')} withinPortal>
                            <ActionIcon
                              variant="subtle"
                              color="gray"
                              size="sm"
                              onClick={() => openEdit(modifier)}
                              aria-label={t('common.edit')}
                            >
                              <IconEdit size={14} stroke={1.6} />
                            </ActionIcon>
                          </Tooltip>
                          <Tooltip label={t('common.delete')} withinPortal>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              size="sm"
                              onClick={() => setDeleting(modifier)}
                              aria-label={t('common.delete')}
                            >
                              <IconTrash size={14} stroke={1.6} />
                            </ActionIcon>
                          </Tooltip>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </Stack>
      </Drawer>

      <ModifierFormModal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        groupId={group.modifierGroupId}
        modifier={editing}
      />

      <ConfirmDialog
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('modifiers.delete.title')}
        message={t('modifiers.delete.body', { name: deleting?.nameAr ?? '' })}
        loading={deleteMutation.isPending}
      />
    </>
  );
}

function formatPriceDelta(delta: number): string {
  // TODO: pull tenant currency. ILS for the pilot.
  if (delta === 0) return '—';
  const sign = delta > 0 ? '+' : '';
  return `${sign}${delta.toFixed(2)} ILS`;
}
